import React from 'react';
import PropTypes from 'prop-types';
import { ToastContext, Modal, Button, SelectHierarchy, Message } from '@weezevent/nacre';
import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import Style from './changeRatePostAction.module.css';
import RatesTreeModel from '../../../models/ratesTreeModel';
import useAppContext from '../../../context/useAppContext';
import LoadingCard from '../../../components/loadingCard';

function SelectHierarchyRates({ options, rateId, onChangeForm }) {
    const { t } = useTranslation();

    return (
        <SelectHierarchy
            options={options}
            searchable
            placeholder={t('ticket.manager.attendees.action.change_rate.ratePlaceholder')}
            placeholderSearch={t('ticket.common.action.search')}
            chevron
            widthIndent={'15px'}
            onChange={value => onChangeForm(value)}
            value={rateId}
        />
    );
}

export default function ChangeRatePostAction({ actionInstance = null, onClose = () => {}, sync = () => {} }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [{ event, organization, feature_flags }] = useAppContext();
    let [rates, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk
        },
        [organization, event]
    );

    const ratesOptions = React.useMemo(() => {
        if (!rates) {
            return [];
        }
        return rates.toOptions({
            filter: rate => rate.id !== actionInstance.items[0].rate.id
        });
    }, [rates, actionInstance]);

    const payableTicket = React.useMemo(() => parseInt(actionInstance.items[0].paid_price) > 0, [actionInstance]);
    const [selectedRate, setSelectedRate] = React.useState(null);

    const data = React.useMemo(() => {
        let _data = {
            rate_id: selectedRate,
            mode_accred: feature_flags.includes('EO_EXTIA'),
            ids: actionInstance.items.map(item => item.id)
        };
        return _data;
    }, [selectedRate, actionInstance, feature_flags]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction(data, actionInstance.items[0])
            .then(() => {
                toast.success(t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.toast.success'));
                setIsSubmitting(false);
                onClose();
                sync();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.toast.error'));
                setIsSubmitting(false);
            });
    }, [actionInstance, onClose, data]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    if (loadingRates) {
        return null;
    }

    return (
        <Modal
            size="medium"
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={actionInstance ? t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.title') : ''}
            subtitle={
                actionInstance ? t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.subtitle', { ticket: actionInstance.items[0].id_weez_ticket }) : ''
            }
            submitButton={<Button primary onClick={handleSubmit} disabled={isSubmitting || !selectedRate} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <LoadingCard loading={loadingRates} height={'20vh'}>
                <SelectHierarchyRates options={ratesOptions} rateId={selectedRate} onChangeForm={setSelectedRate} />
                <div className={Style['change-rate']}>
                    {payableTicket && actionInstance.items[0].order.status === 'valid' && (
                        <Message type="danger" title={t('ticket.common.warning')}>
                            {t('ticket.manager.attendees.action.change_rate.warning')}
                        </Message>
                    )}
                </div>
            </LoadingCard>
        </Modal>
    );
}

ChangeRatePostAction.propTypes = {
    actionInstance: PropTypes.object,
    onClose: PropTypes.func,
    sync: PropTypes.func
};
